import type { ToolPart } from '../../types';
import { useLayoutStore } from '../../layout/LayoutProvider';

interface Props {
  part: ToolPart;
}

interface DraftInput {
  subject?: string;
}

/**
 * Inline renderer for `compose_email` tool calls.
 *
 * Same idea as CanvasCard: a compact clickable chip in the chat stream
 * that lets the user jump back to the composer if they closed it.
 * `compose_email` is a state-canvas tool — once called, the draft lives
 * in `state.email_draft` and the user can keep editing it across turns.
 * Auto-open is centralised in ChatPage's `state.email_draft` watcher.
 */
export function MailComposeCard({ part }: Props) {
  const { mainView, openMain } = useLayoutStore();

  const input = (part.state.input ?? {}) as DraftInput;
  const subject = input.subject?.trim() || 'без темы';
  const isActive = part.state.status === 'running';
  const isError = part.state.status === 'error';
  const isOpen = mainView?.id === 'mail-compose';
  const dotClass = isError
    ? 'tool-dot-error'
    : isActive
      ? 'tool-dot-active'
      : 'tool-dot-done';

  const label = isActive
    ? `Готовлю черновик: ${subject}`
    : isError
      ? 'Не удалось подготовить черновик'
      : `Черновик письма: ${subject}`;

  const handleClick = () => {
    if (!isOpen) openMain('mail-compose');
  };

  const hint = isOpen ? 'Открыт' : 'Открыть';

  return (
    <div
      className={`tool-step tool-${part.state.status} canvas-card${isOpen ? ' canvas-card--open' : ''}`}
      onClick={isError ? undefined : handleClick}
      role={isError ? undefined : 'button'}
      tabIndex={isError ? undefined : 0}
      onKeyDown={(e) => { if (!isError && e.key === 'Enter') handleClick(); }}
      title={isError ? undefined : (isOpen ? 'Редактор открыт' : 'Открыть редактор')}
    >
      <div className="tool-step-rail">
        <div className={`tool-step-dot ${dotClass}`} />
      </div>
      <div className="tool-step-body canvas-card-body">
        <div className={`tool-step-label ${isActive ? 'tool-active' : ''}`}>
          {label}
        </div>
        {isError && part.state.error && (
          <div className="tool-step-content">
            <pre className="tool-error">{part.state.error}</pre>
          </div>
        )}
      </div>
      {!isError && (
        <div className="canvas-card-cta" aria-hidden>
          <span className="canvas-card-cta-text">{hint}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
            <path d="M12 5l7 7-7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
